import { HttpError, api } from '@/api/client'
import type { Schemas } from '@/api/client'
import { useAuthStore } from '@/stores/auth.store'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'

export function useAuth() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const setAuth = useAuthStore((s) => s.setAuth)
  const clearAuth = useAuthStore((s) => s.clearAuth)
  const user = useAuthStore((s) => s.user)
  const org = useAuthStore((s) => s.org)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  const login = useMutation({
    mutationFn: async (body: Schemas['LoginRequest']) => {
      const tokens = await api.auth.login(body)
      useAuthStore.getState().setTokens(tokens)
      const me = await api.auth.me()
      return { tokens, me }
    },
    onSuccess: ({ tokens, me }) => {
      setAuth(me.user, me.org, me.permissions, tokens)
      queryClient.clear()
    },
  })

  const logout = useMutation({
    mutationFn: async () => {
      try {
        await api.auth.logout()
      } catch {
        // even if server rejects, we clear local state
      }
    },
    onSettled: () => {
      clearAuth()
      queryClient.clear()
      void navigate({ to: '/auth/login' })
    },
  })

  return { user, org, isAuthenticated, login, logout }
}

export { HttpError }
